import type { ReactNode } from 'react';

import type { CellRenderContext } from '@/components/data-table';
import { getDeviceDisabledTooltip } from '@/features/equipment-browser/equipment-bulk-edit/equipment-bulk-edit.helpers';
import type {
  EquipmentBulkEditTableCellContent,
  EquipmentBulkEditTableData,
  EquipmentBulkEditTableIconCellContent,
} from '@/features/equipment-browser/equipment-bulk-edit/equipment-bulk-edit.types';

import { EquipmentBulkEditTableIconCell } from './equipment-bulk-edit-table-icon-cell';
import { EquipmentBulkEditTableSettingCell } from './equipment-bulk-edit-table-setting-cell';

interface EquipmentBulkEditTableCellProps {
  context: CellRenderContext<EquipmentBulkEditTableData, EquipmentBulkEditTableCellContent>;
  disabledByPermissions?: boolean;
  isSettingDisabled: boolean;
  permissionTooltipText?: string;
  showIcon: boolean;
}

export const EquipmentBulkEditTableCell = ({
  context,
  disabledByPermissions,
  isSettingDisabled,
  permissionTooltipText,
  showIcon,
}: EquipmentBulkEditTableCellProps): ReactNode => {
  const disabledTooltipText =
    getDeviceDisabledTooltip(context.row.original) ??
    (disabledByPermissions ? permissionTooltipText : undefined);

  const rowId = String(context.row.original.deviceId?.value ?? '');
  const content = context.value;

  // Branch 1: Icon cell (first column with device icon)
  if (showIcon && content?.deviceId) {
    return (
      <EquipmentBulkEditTableIconCell content={content as EquipmentBulkEditTableIconCellContent} />
    );
  }

  // Branch 2: Setting cell (all other columns)
  return (
    <EquipmentBulkEditTableSettingCell
      actionComponent={content?.actionComponent}
      columnId={context.column.id}
      disabled={isSettingDisabled}
      disabledByPermissions={disabledByPermissions}
      disabledTooltipText={disabledTooltipText}
      displayType={content?.displayType}
      displayUnits={content?.displayUnits}
      dropdownOptions={content?.dropdownOptions}
      enumOptions={content?.enumOptions}
      permissionTooltipText={permissionTooltipText}
      readonlyValue={content?.value != null ? String(content.value) : undefined}
      rowId={rowId}
      units={content?.units}
    />
  );
};
